import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@common";
import { MAX_FILTER_OPTIONS, DESC_ASC_LABEL_KEYS } from "@utils/associateFee";
import { getFilterLabel } from "@utils/associateFee";
import { useFilterValue, useIsMenuOpen, useSortDirection } from "@hooks/useHeaderMenuStore";

const AssociateFeeHeaderMenu = memo(function AssociateFeeHeaderMenu({
  column,
  rows,
  onSortChange,
  onFilterChange,
  menuStore,
  totalDisplay,
  totalBadgeClassName,
}) {
  const isOpen = useIsMenuOpen(menuStore, column.key);
  const sortDirection = useSortDirection(menuStore, column.key);
  const selectedSet = useFilterValue(menuStore, column.key);
  const [search, setSearch] = useState("");
  const [menuPosition, setMenuPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const isFiltered = !!selectedSet;
  const columnLabel = typeof column.header === "string" ? column.header : column.key;
  const useDescAscLabels = DESC_ASC_LABEL_KEYS.has(column.key);
  const isSortApplied = !!sortDirection;
  const canDirectSort = !["pdf", "email", "_select", "practitioner_name"].includes(column.key);

  useEffect(() => {
    if (!isOpen) return undefined;

    function updatePosition() {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 288;
      const viewportWidth = window.innerWidth;
      const left = Math.min(Math.max(12, rect.left), Math.max(12, viewportWidth - menuWidth - 12));

      setMenuPosition({
        top: rect.bottom + 8,
        left,
      });
    }

    function handleClickOutside(event) {
      if (!menuRef.current?.contains(event.target) && !buttonRef.current?.contains(event.target)) {
        menuStore.setOpenKey(null);
      }
    }

    updatePosition();
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, menuStore]);

  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setMenuPosition(null);
  }, [isOpen]);

  const allOptions = useMemo(() => {
    const counts = new Map();

    rows.forEach((row) => {
      const label = getFilterLabel(row, column.key);
      counts.set(label, (counts.get(label) || 0) + 1);
    });

    return [...counts.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
      .slice(0, MAX_FILTER_OPTIONS);
  }, [rows, column.key]);

  const visibleOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return allOptions;
    return allOptions.filter((opt) => opt.label.toLowerCase().includes(query));
  }, [allOptions, search]);

  const isChecked = useCallback(
    (label) => (selectedSet ? selectedSet.has(label) : true),
    [selectedSet],
  );

  const allVisibleSelected =
    visibleOptions.length > 0 && visibleOptions.every((opt) => isChecked(opt.label));

  const applyNextSelection = useCallback(
    (next) => {
      if (next.size === allOptions.length) {
        onFilterChange(column.key, null);
        return;
      }
      onFilterChange(column.key, next);
    },
    [allOptions.length, column.key, onFilterChange],
  );

  const toggleValue = useCallback(
    (label, checked) => {
      const next = new Set(selectedSet ?? allOptions.map((opt) => opt.label));
      if (checked) next.add(label);
      else next.delete(label);
      applyNextSelection(next);
    },
    [selectedSet, allOptions, applyNextSelection],
  );

  const toggleAllVisible = useCallback(
    (checked) => {
      const next = new Set(selectedSet ?? allOptions.map((opt) => opt.label));
      visibleOptions.forEach((opt) => {
        if (checked) next.add(opt.label);
        else next.delete(opt.label);
      });
      applyNextSelection(next);
    },
    [selectedSet, allOptions, visibleOptions, applyNextSelection],
  );

  const handleDirectSortToggle = useCallback(() => {
    const nextDirection = sortDirection === "asc" ? "desc" : "asc";
    onSortChange(column.key, nextDirection);
  }, [column.key, onSortChange, sortDirection]);

  return (
    <div className="flex w-full min-w-0 items-center gap-1 relative">
      {totalDisplay && (
        <div
          className={`absolute rounded-t-xl top-[-28px] left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-0.5 text-xs font-semibold text-white tracking-normal ${totalBadgeClassName ?? "bg-palette-indigo-badge"}`}
        >
          {totalDisplay}
        </div>
      )}
      <div ref={buttonRef} className="flex w-full min-w-0 flex-1 flex-col items-stretch">
        <button
          type="button"
          onClick={() => menuStore.setOpenKey(isOpen ? null : column.key)}
          className={[
            "flex w-full min-w-0 items-center rounded-md px-1.5 py-1 text-left transition-colors",
            isSortApplied
              ? "bg-palette-indigo-soft text-palette-indigo-heading"
              : isFiltered
                ? "bg-palette-violet-soft text-palette-violet-heading"
                : "",
          ].join(" ")}
        >
          <span className={`truncate ${isSortApplied ? "font-semibold" : ""}`}>
            {column.header}
          </span>
        </button>
      </div>

      <button
        type="button"
        onClick={handleDirectSortToggle}
        disabled={!canDirectSort}
        className={[
          "inline-flex h-5 w-5 shrink-0 self-center items-center justify-center rounded transition-colors",
          isSortApplied
            ? "bg-palette-indigo-muted text-palette-indigo-badge hover:bg-palette-indigo-border"
            : isFiltered
              ? "bg-palette-violet-muted text-palette-violet-badge hover:bg-palette-violet-border"
              : "text-palette-gray-border hover:bg-palette-gray-subtle hover:text-palette-gray-secondary",
          !canDirectSort ? "cursor-default opacity-40 hover:bg-transparent" : "",
        ].join(" ")}
        title={useDescAscLabels ? "Toggle sort ascending/descending" : "Toggle sort A-Z/Z-A"}
      >
        <span className="text-xs font-semibold">
          {sortDirection === "asc" ? "↑" : sortDirection === "desc" ? "↓" : isFiltered ? "•" : "▾"}
        </span>
      </button>

      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] w-72 rounded-lg border border-palette-gray-divider bg-white p-3 text-left shadow-xl"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            <div className="mb-2 flex flex-wrap gap-2">
              <Button
                variant={sortDirection === "asc" ? "primary" : "outline"}
                size="xs"
                className="!px-2"
                onClick={() => onSortChange(column.key, "asc")}
              >
                {useDescAscLabels ? "Asc" : "A-Z"}
              </Button>
              <Button
                variant={sortDirection === "desc" ? "primary" : "outline"}
                size="xs"
                className="!px-2"
                onClick={() => onSortChange(column.key, "desc")}
              >
                {useDescAscLabels ? "Desc" : "Z-A"}
              </Button>
              {(sortDirection || isFiltered) && (
                <Button
                  variant="ghost"
                  size="xs"
                  className="!px-2"
                  onClick={() => {
                    onSortChange(column.key, null);
                    onFilterChange(column.key, null);
                  }}
                >
                  Clear
                </Button>
              )}
            </div>

            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${String(columnLabel).toLowerCase()}...`}
              className="mb-2 w-full rounded-md border border-palette-gray-border px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-2 focus:ring-palette-indigo-border"
            />

            <div className="mb-2 border-b border-palette-gray-subtle pb-2">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-palette-gray-primary">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(e) => toggleAllVisible(e.target.checked)}
                  className="h-4 w-4 rounded border-palette-gray-border"
                />
                <span>{`Select all (${visibleOptions.length})`}</span>
              </label>
            </div>

            <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
              {visibleOptions.length === 0 ? (
                <p className="text-sm text-palette-gray-secondary">No matching values.</p>
              ) : (
                visibleOptions.map((opt) => (
                  <label
                    key={opt.label}
                    className="flex cursor-pointer items-start gap-2 text-sm text-palette-gray-primary"
                  >
                    <input
                      type="checkbox"
                      checked={isChecked(opt.label)}
                      onChange={(e) => toggleValue(opt.label, e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-palette-gray-border"
                    />
                    <span>{`${opt.label} (${opt.count})`}</span>
                  </label>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
});

export default AssociateFeeHeaderMenu;
